import React from 'react';
import { Helmet } from 'react-helmet-async';
import { siteConfig } from '../data/site-content';
import { CheckCircle2, ArrowRight, XCircle, Brain, Target, ShieldCheck, Quote, Calendar, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

const MetakognitivTerapi = () => {
  return (
    <>
      <Helmet>
        <title>Metakognitiv terapi i Valby – {siteConfig.name}</title>
        <meta name="description" content="Lær at give tankerne mindre plads med metakognitiv terapi. En effektiv metode mod angst, stress og tankemylder hos Lars Nissen Corell – Psykoterapeut MPF." />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 bg-[#F4EFE7]/30">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-4">Behandlingsform</span>
              <h1 className="text-4xl md:text-5xl font-serif text-[#123F13] mb-8">Metakognitiv terapi</h1>
              <p className="text-xl text-[#3B352F]/80 leading-relaxed mb-8">
                Hvis tankerne fylder for meget, handler det ikke om at forstå dem endnu mere. Det handler om at lære at give dem mindre plads, så du kan få ro og overskud tilbage.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/bestil-tid" className="bg-[#193f10] text-white px-6 py-3 rounded-md font-bold hover:bg-[#193f10]/90 transition-all flex items-center gap-2 shadow-md">
                  <Calendar size={18} /> Book første behandlingssession
                </Link>
                <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#193f10] text-[#193f10] px-6 py-3 rounded-md font-bold hover:bg-[#faf7f2] transition-all">
                  <Phone size={18} /> Tlf. 27 20 46 71
                </a>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="relative">
                <div className="absolute inset-0 bg-[#123F13]/5 rounded-2xl transform translate-x-3 translate-y-3 -z-10"></div>
                <img 
                  src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/8139e572-b279-47eb-85e7-ac9aa29443d7.png" 
                  alt="Metakognitiv terapi i Valby" 
                  className="w-full h-auto object-cover rounded-2xl shadow-lg border border-[#E8DDCE]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial & Recognition Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 space-y-20">
          <div className="bg-[#123F13] text-white p-8 md:p-12 rounded-2xl relative overflow-hidden">
            <Quote className="absolute top-8 right-8 text-white/10" size={120} />
            <div className="relative z-10 max-w-2xl">
              <h3 className="text-2xl font-serif mb-6 italic">“Lars har lært mig at holde afstand til grublerier og bekymringer”</h3>
              <p className="text-white/80 mb-6 leading-relaxed">
                Hvad der før fyldte det meste af min dag, fylder i dag ingenting. Jeg kan næsten ikke få armene ned over det gode resultat.
              </p>
              <p className="font-bold">— Louise, tidligere klient</p>
              <p className="text-white/60 text-sm mt-4 italic">Louise fik ro og overskud tilbage med metakognitiv terapi.</p>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-serif text-[#123F13] mb-10">Måske genkender du det her?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  title: "Du tænker meget og har svært ved at slappe af",
                  desc: "Du forsøger måske at forstå dine tanker, følelser eller reaktioner – men ender med at bruge endnu mere tid i hovedet."
                },
                {
                  title: "Du forsøger at få kontrol over tankerne",
                  desc: "Du prøver at stoppe dem, ændre dem eller tænke positivt, men de vender ofte tilbage med mere styrke."
                },
                {
                  title: "Du mærker uro i kroppen",
                  desc: "Kroppen reagerer med spænding, hjertebanken eller rastløshed, hvilket sætter gang i endnu flere tanker."
                },
                {
                  title: "Du føler dig træt i sindet",
                  desc: "Selvom andre ikke kan se det, bruger du enormt meget energi på bekymring, selvkritik eller overblik."
                }
              ].map((item, idx) => (
                <div key={idx} className="bg-[#F4EFE7]/30 p-6 rounded-xl border border-[#E8DDCE]">
                  <h3 className="font-bold text-[#123F13] mb-3">{item.title}</h3>
                  <p className="text-[#3B352F]/70 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 text-center text-[#3B352F]/60 italic">
              "Der er ikke noget galt med dig. Det handler ofte om, hvordan opmærksomheden er kommet til at arbejde – og det kan trænes."
            </p>
          </div>
        </div>
      </section>

      {/* Method Section */}
      <section className="py-20 bg-[#F4EFE7]/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="prose prose-lg text-[#3B352F]/70 max-w-none">
            <h2 className="text-3xl font-serif text-[#123F13] mb-8">En anden måde at arbejde med tanker på</h2>
            <p className="mb-6">
              I metakognitiv terapi arbejder jeg ikke med at analysere indholdet af dine tanker. Jeg hjælper dig i stedet med at undersøge, hvordan du forholder dig til dem. De fleste mennesker forsøger at tænke sig ud af problemerne, men ender med at bruge endnu mere energi på bekymringer og grublerier.
            </p>
            <p className="mb-12">
              Du behøver ikke finde svar på alle tanker. Det handler om at opdage, hvornår du bliver fanget i bekymring – og lære at lade tankerne være, så de kan passere af sig selv.
            </p>
            <p className="mb-12">
              Metakognitiv terapi er også særdeles velegnet til autistiske personer, da metoden arbejder med konkrete strategier og ikke kræver, at du skal tale om eller analysere svære følelser på en bestemt måde. Læs mere om mit <Link to="/autismevenlig-terapi" className="text-[#8C2F12] font-semibold hover:underline">autismevenlige forløb</Link>.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-12 not-prose">
              <div className="bg-white p-8 rounded-xl border border-[#E8DDCE]">
                <h4 className="font-bold text-[#123F13] mb-4">Det handler ikke om</h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <XCircle size={18} className="text-[#8C2F12] mt-1 shrink-0" />
                    <span>At tømme hovedet for tanker</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle size={18} className="text-[#8C2F12] mt-1 shrink-0" />
                    <span>At tænke positivt hele tiden</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle size={18} className="text-[#8C2F12] mt-1 shrink-0" />
                    <span>At finde den perfekte forklaring</span>
                  </li>
                </ul>
              </div>
              <div className="bg-[#123F13] p-8 rounded-xl text-white">
                <h4 className="font-bold mb-4">Det handler om</h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={18} className="text-[#E8DDCE] mt-1 shrink-0" />
                    <span>At opdage hvornår du bliver fanget</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={18} className="text-[#E8DDCE] mt-1 shrink-0" />
                    <span>At lære at lade tankerne være</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={18} className="text-[#E8DDCE] mt-1 shrink-0" />
                    <span>At bruge din energi frit igen</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specialist Info */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-[#123F13] text-white p-10 rounded-2xl">
            <div className="flex flex-col md:flex-row gap-10 items-center">
              <div className="shrink-0">
                <img 
                  src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/corehypnose-lak-segl.png" 
                  alt="Core Kvalitetsstempel" 
                  className="w-32 h-32 rotate-[-6deg] drop-shadow-[0_10px_20px_rgba(0,0,0,0.3)] object-contain"
                />
              </div>
              <div>
                <h2 className="text-2xl font-serif mb-4">Høj faglig specialist-viden</h2>
                <p className="text-white/80 mb-6">
                  Når du vælger metakognitiv terapi hos Core Hypnose, møder du en behandler med højeste certificering og mange års erfaring.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-white/90">
                  <li className="flex items-center gap-2">
                    <Target size={16} className="text-[#E8DDCE]" />
                    Psykoterapeut MPF
                  </li>
                  <li className="flex items-center gap-2">
                    <Target size={16} className="text-[#E8DDCE]" />
                    Certificeret metakognitiv psykoterapeut, Neokognitivt Institut (Level IV – højeste niveau)
                  </li>
                  <li className="flex items-center gap-2">
                    <Target size={16} className="text-[#E8DDCE]" />
                    Certificeret metakognitiv supervisor og hypnoterapeut
                  </li>
                  <li className="flex items-center gap-2">
                    <Target size={16} className="text-[#E8DDCE]" />
                    26+ års erfaring
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* First Session Info */}
      <section className="py-20 bg-[#F4EFE7]/30">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-serif text-[#123F13] mb-10 text-center">Hvad sker der i første session?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <ShieldCheck className="text-[#8C2F12]" />
              </div>
              <h4 className="font-bold mb-2">Overblik</h4>
              <p className="text-sm text-[#3B352F]/70">Vi kortlægger din situation og finder ud af, hvad der vedligeholder dit tankemylder.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <Target className="text-[#8C2F12]" />
              </div>
              <h4 className="font-bold mb-2">Konkrete værktøjer</h4>
              <p className="text-sm text-[#3B352F]/70">Du får de første metakognitive redskaber med hjem, som du kan bruge med det samme.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <ArrowRight className="text-[#8C2F12]" />
              </div>
              <h4 className="font-bold mb-2">En klar plan</h4>
              <p className="text-sm text-[#3B352F]/70">Vi lægger en plan for dit videre forløb, så du ved præcis, hvad næste skridt er.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-serif text-[#123F13] mb-6">Klar til at få ro og overblik igen?</h2>
          <p className="text-lg text-[#3B352F]/70 mb-10">
            De fleste mærker en forskel allerede efter første session. Book din tid i dag.
          </p>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#123F13] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#123F13]/90 transition-all">
            Book første session (375 kr) <ArrowRight size={18} />
          </Link>
          <p className="mt-6 text-sm text-[#3B352F]/50 italic">
            Sessionen kan foregå i klinikken i Valby eller online.
          </p>
        </div>
      </section>
    </>
  );
};

export default MetakognitivTerapi;
